import styled from 'styled-components';

import {
  paradigmLightBlue,
  portage15,
} from '@paradigm/design-system/src/colors';
import Button from '@paradigm/design-system/src/components/Button';

import { CreditPack } from '#/api/billing';

import { Card, Hint, SectionTitle, settingsTextPrimary } from './styles';

interface Props {
  readonly packs: readonly CreditPack[];
  readonly pending: boolean;
  readonly canBuyCredits: boolean;
  readonly onBuy: (priceId: string) => void;
}

export default function CreditPackCard({
  packs,
  pending,
  canBuyCredits,
  onBuy,
}: Props) {
  if (packs.length === 0) {
    return (
      <Card data-testid="billing-credit-pack-card">
        <SectionTitle>Credit packs</SectionTitle>
        <Hint>No credit packs are configured.</Hint>
      </Card>
    );
  }

  return (
    <Card data-testid="billing-credit-pack-card">
      <SectionTitle>Buy credits</SectionTitle>
      {!canBuyCredits && (
        <Hint data-testid="billing-credit-pack-locked-message">
          An active subscription is required to purchase credit packs.
        </Hint>
      )}
      <Packs>
        {packs.map((pack) => (
          <Pack key={pack.price_id}>
            <PackInfo>
              <PackName>{pack.name}</PackName>
              <PackAmount>
                {pack.amount_usd}
                <PackCreditsLabel> credits</PackCreditsLabel>
              </PackAmount>
            </PackInfo>
            <Button
              ariaLabel={`Buy ${pack.name}`}
              theme="secondary-light-blue"
              size="small"
              disabled={pending || !canBuyCredits}
              onClick={() => onBuy(pack.price_id)}
            >
              Buy
            </Button>
          </Pack>
        ))}
      </Packs>
    </Card>
  );
}

const Packs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Pack = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${portage15};
  border-radius: 4px;
`;

const PackInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PackName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${settingsTextPrimary};
`;

const PackAmount = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.1;
  color: ${settingsTextPrimary};
`;

const PackCreditsLabel = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${paradigmLightBlue};
  opacity: 0.8;
`;
